import httpClient from './httpClient'

// Both endpoints live in ExchangeAnalyticsService, which serves under the
// /api/v1/exchange-analytics ingress — hence the /exchange-analytics prefix on the
// default http client (the bare /api/v1/reporting/... prefix belongs to
// ReportingService). Wiring guide: docs/MARKET_CONTEXT_WIRING.md
const END_POINT = '/exchange-analytics/buyer'

const MarketContextAPI = {
  // One row per campaign, whatever its routing type, positioned against its market cell's
  // winning-bid distribution over a TRAILING WINDOW WHOSE LENGTH VARIES: 24 hours on six
  // days of the week, 168 hours on Mondays (America/Los_Angeles), because a Monday 24h
  // window looks back at Sunday and the weekend volume dip makes those percentiles swing.
  // The {rows, truncated, limit, windowHours} envelope reports the length actually used, and
  // it is present even when rows is empty - never assume 24. Every count and percentile on
  // every row is measured over it, which is why the same campaign can carry a different
  // tier, rankBand and missed-call count on Monday than on Tuesday.
  //
  // Three row states, told apart by positionedBidInUSD: a Static campaign positioned on its
  // configured max bid; an RTB/Programmatic campaign positioned on the median bid observed
  // over the window; and a dynamic campaign that placed no bids in the window, whose market
  // half (percentiles, marketActivity) is populated while positionedBidInUSD, tier, rankBand,
  // suggestedBidInUSD and estimatedAdditionalCallsPerDay are all null - null rather than
  // zero, because it did not bid at all. WHICH of the last two states a dynamic campaign
  // lands in can itself change with the window: one that bid five days ago but is paused
  // today is positioned on Mondays and bid-less on Tuesdays.
  //
  // From the 2026-10-30 follow-ups (docs/MARKET_CONTEXT_SUGGESTED_BID_REPLAY_FOLLOWUPS.md), a row
  // is positioned on the delivered calls OTHER buyers won, and two more nulls ride beside a
  // positioned bid: tier and the three quartiles are null when those calls are under 30 in the
  // row's window and in the last 7 days (R55, R57), and rankBand is null when fewer than 2 other
  // buyers bid in the cell over the window (R57). The row keeps positionWindowHours, missedCalls
  // and currentTerms, and never carries a suggestion then. The `contested` counts stay on the wire
  // and are not read (R54). From R59 (same day) a row with no suggestion may carry
  // `losesOnConversion` true: over 7 days the campaign lost at least 10% of its contested auctions
  // to other accounts, more than half of them to a winner with a higher stored conversion. From R62
  // (2026-10-31) such a row may also carry `suggestedSetup`, the conversion-compensating one, of the
  // same shape as any other row's (`minimumBucket` null, `suggestedBidInUSD` null, so the flag-off
  // chip is unchanged): the list shows it as a chip or as advice with the conversion note, and the
  // card leads with it. Without one the list says "Improve the conversion rate", and nothing about
  // the bid. A row with a market suggestion has `losesOnConversion` false. Null while the backend's
  // Slice H reads were unavailable.
  //
  // `rankBand` is still sent and no longer read (R60, the same day): the list's Opportunity column
  // stopped showing the band, which ranked the bid on a different population from the badge and
  // contradicted it on half the replay's rows, and says why a row has no suggestion instead.
  //
  // A campaign is omitted only when its market cell had no auctions in the window - so a
  // thin cell with auctions last week but none yesterday is omitted on the short window and
  // present on the long one. A positioned campaign that merely didn't bid still gets a full
  // row with zeroed missed/estimate fields.
  async getCampaignMarketPositions(accountId) {
    return httpClient.get(`${END_POINT}/${accountId}/campaigns/market-positions`)
  },

  // 7d market context for one category, optionally narrowed by country/language
  // (omitted = aggregate across). Bare object (no envelope). An empty market cell
  // returns null percentiles, tiers: [] and winRateCurve: null — the panel stays hidden.
  // From ExchangeAnalyticsService's backend PR 1 it also carries the market card's fields
  // (scope, insufficientData, measuredAtUnix and suggestion, the suggested bid and minimum),
  // which the card shows even for an empty cell (docs/MARKET_CONTEXT_SUGGESTED_BID_ITERATION_1.md §5).
  async getCategoryMarketContext(accountId, { categoryId, countryId, languageId }) {
    return httpClient.get(`${END_POINT}/${accountId}/market-context`, {
      params: { categoryId, countryId, languageId }
    })
  }
}

export default MarketContextAPI
